import { showToastWarning } from "../../../utils/showToast";
import { LocalizationKeys } from "../../../i18n";

/**
 * Утилиты для работы с mock данными
 */

/**
 * Выполняет API запрос с fallback на mock данные в случае ошибки
 *
 * @param apiCall - функция, выполняющая API запрос
 * @param getMockData - функция, возвращающая mock данные
 * @param logContext - контекст для логирования (например, 'reviews for content-123')
 * @returns результат API запроса или mock данные при ошибке
 *
 * @example
 * const data = await fetchWithMockFallback(
 *   () => apiClient<ReviewsResponse>(url),
 *   () => getMockReviews(),
 *   'reviews for content-123'
 * )
 */
export async function fetchWithMockFallback<T>(
  apiCall: () => Promise<T>,
  getMockData: () => T,
  logContext: string,
): Promise<T> {
  try {
    return await apiCall();
  } catch (error) {
    console.warn(`Failed to fetch ${logContext}, using mock data:`, error);
    showToastWarning(LocalizationKeys.TOAST_DEV_MOCK_DATA_LOADED);
    return getMockData();
  }
}

/**
 * Выполняет API запрос с условным fallback на mock данные
 * Если mock данные отсутствуют, пробрасывает ошибку
 *
 * @param apiCall - функция, выполняющая API запрос
 * @param getMockData - функция, возвращающая mock данные или undefined
 * @param logContext - контекст для логирования
 * @returns результат API запроса или mock данные при ошибке
 * @throws ошибку API, если mock данные не найдены
 *
 * @example
 * const data = await fetchWithOptionalMock(
 *   () => apiClient<ReviewDetails>(url),
 *   () => getMockReviewById(reviewId),
 *   `review ${reviewId}`
 * )
 */
export async function fetchWithOptionalMock<T>(
  apiCall: () => Promise<T>,
  getMockData: () => T | undefined,
  logContext: string,
): Promise<T> {
  try {
    return await apiCall();
  } catch (error) {
    const mockData = getMockData();

    if (mockData) {
      console.warn(`Failed to fetch ${logContext}, using mock data:`, error);
      showToastWarning(LocalizationKeys.TOAST_DEV_MOCK_DATA_LOADED);
      return mockData;
    }

    // Если mock данных нет, пробрасываем ошибку
    throw error;
  }
}
